import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import {
  ISO_DAYS,
  REVERSE_ISO_DAYS,
  SHOP_TYPE_PERIODIC,
  TYPE_MARKET_INSIGHT,
} from 'frontend-utils/utils/constants/constant-values';

interface GeneralReportScheduleWeekdaySelectionSignature {
  Args: {
    reportType: unknown;
    shopType: unknown;
    updateSelectedWeekdays: unknown;
  };
}

export default class GeneralReportScheduleWeekdaySelection extends Component<GeneralReportScheduleWeekdaySelectionSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get frequencyString() {
    if (this.isWeekdaySelectionDisabled) {
      let res = '';
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const days = this.args.selectedWeekdays;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (days.length && days.length !== 7) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        if (days.length === 2 && days.includes('sat') && days.includes('sun')) {
          res = this.intl.t('reports.frequency_weekends_short');
        } else if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          days.length === 5 &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.includes('mon') &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.includes('tue') &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.includes('wed') &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.includes('thu') &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.includes('fri')
        ) {
          res = this.intl.t('reports.frequency_weekdays_short');
        } else {
          // @ts-expect-error: Incorrect type
          const dayStrings = [];
          let dayStringResult = '';

          const sortOrder = {
            sun: 0,
            mon: 1,
            tue: 2,
            wed: 3,
            thu: 4,
            fri: 5,
            sat: 6,
          };

          if (dayjs.localeData().firstDayOfWeek() === ISO_DAYS.mon) {
            sortOrder.sun = 7;
          }

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.sort((a, b) => sortOrder[a] > sortOrder[b]);

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          days.forEach((day) => {
            dayStrings.push(this.intl.t(`days.${day}`));
          });
          // @ts-expect-error: Incorrect type
          dayStringResult += dayStrings.join(', ');

          res += ` ${this.intl.t('general.on_date', { days: dayStringResult })} `;
        }
      } else {
        res = this.intl.t('reports.frequency_daily_short');
      }
      return res;
    }
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketInsightReport() {
    return this.args.shopType === TYPE_MARKET_INSIGHT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isWeekdaySelectionDisabled() {
    // You can't choose when to receive periodic scheduled reports except for Market Insight
    return (
      this.args.reportType === SHOP_TYPE_PERIODIC && !this.isMarketInsightReport
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekdays() {
    const weekdays = [];
    const canEditReports =
      // @ts-expect-error: Incorrect type
      !this.args.reportgroup ||
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.access.can('report.edit', this.args.reportgroup);

    let dow = dayjs().startOf('week');
    while (dow.isSameOrBefore(dayjs().endOf('week'))) {
      const isoWeekday = REVERSE_ISO_DAYS[dow.isoWeekday()];
      const weekdayObj = {
        isoWeekday,
        shortDayName: dow.format('dd')[0], // Initial dow letter
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        isSelected: this.args.selectedWeekdays.includes(isoWeekday),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        isEnabled:
          !this.isWeekdaySelectionDisabled &&
          canEditReports &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          this.args.enabledDays.includes(isoWeekday),
      };
      weekdays.push(weekdayObj);

      dow = dow.add(1, 'day');
    }

    return weekdays;
  }

  @action
  // @ts-expect-error: Incorrect type
  onDayClicked(clickedWeekday): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const isLastSelectedItem =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.selectedWeekdays.length === 1 && clickedWeekday.isSelected;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (clickedWeekday.isEnabled && !isLastSelectedItem) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.updateSelectedWeekdays(clickedWeekday.isoWeekday);
    }
  }

  <template>
    {{#each this.weekdays as |weekday|}}
      <div
        class="weekday-btn
          {{if weekday.isSelected 'active'}}
          {{unless weekday.isEnabled 'disabled'}}"
        role="button"
        {{on "click" (fn this.onDayClicked weekday)}}
      >
        {{weekday.shortDayName}}
      </div>
    {{/each}}
    {{#if this.isWeekdaySelectionDisabled}}
      <EmberTooltip @tooltipClass="default-tooltip default-tooltip--large">
        {{t "reports.report_frequency_warning" frequency=this.frequencyString}}
      </EmberTooltip>
    {{/if}}
  </template>
}
